import { Avatar, Chip, List, ListItem, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ChainNode } from '..';
import { useI18n } from '../../../shared/i18n';
import { ProfileStatusBadge } from '../../../shared/ui/ProfileStatusBadge';

interface ChainListProps {
  chain: ChainNode[];
  currentUserId: string;
}

const ChainContainer = styled(List)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1.5),
  padding: 0,
}));

const ChainItem = styled(ListItem)(({ theme }) => ({
  display: 'block',
  padding: theme.spacing(1.75, 2),
  borderRadius: theme.shape.surface,
  border: `1px solid ${theme.vars!.palette.surface.panelBorder}`,
  background: theme.vars!.palette.surface.soft,
}));

const CurrentUserChainItem = styled(ChainItem)(({ theme }) => ({
  borderColor: theme.vars!.palette.primary.main,
  background: theme.vars!.palette.surface.primarySoft,
}));

const IndexAvatar = styled(Avatar)(({ theme }) => ({
  width: 38,
  height: 38,
  background: theme.vars!.palette.surface.primarySoft,
  color: theme.palette.primary.light,
  fontWeight: 700,
}));

const ChainNodeRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const ChainNodeContent = styled(Stack)(({ theme }) => ({
  flexGrow: 1,
  minWidth: 0,
  gap: theme.spacing(0.5),
}));

const ChainNodeHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ChainNodeId = styled(Typography)({
  fontWeight: 700,
  wordBreak: 'break-word',
});

const ChainNodeStatus = styled(Stack)(({ theme }) => ({
  alignItems: 'flex-end',
  gap: theme.spacing(0.75),
}));

export function ChainList({ chain, currentUserId }: ChainListProps) {
  const { t } = useI18n();
  const orderedChain = [...chain].reverse();

  return (
    <ChainContainer disablePadding>
      {orderedChain.map((node, index) => {
        const isCurrentUser = node.userId === currentUserId;
        const ChainItemComponent = isCurrentUser
          ? CurrentUserChainItem
          : ChainItem;

        return (
          <ChainItemComponent key={node.userId} disablePadding>
            <ChainNodeRow>
              <IndexAvatar>{index + 1}</IndexAvatar>
              <Avatar src={node.avatarUrl ?? undefined}>
                {node.userId.trim().charAt(0).toUpperCase()}
              </Avatar>
              <ChainNodeContent>
                <ChainNodeHeader>
                  <ChainNodeId>{node.userId}</ChainNodeId>
                  {isCurrentUser ? (
                    <Chip
                      color="primary"
                      label={t('chain.you')}
                      size="small"
                      variant="filled"
                    />
                  ) : null}
                  {index === 0 ? (
                    <Chip
                      color="secondary"
                      label={t('chain.topNode')}
                      size="small"
                      variant="outlined"
                    />
                  ) : null}
                </ChainNodeHeader>
                <Typography variant="body2" color="text.secondary">
                  {node.isVerified
                    ? t('chain.verifiedNode')
                    : t('chain.unverifiedNode')}
                </Typography>
              </ChainNodeContent>
              <ChainNodeStatus>
                <ProfileStatusBadge kycLevel={node.kycLevel} />
                <Chip
                  color={node.isVerified ? 'success' : 'default'}
                  label={node.isVerified ? t('common.yes') : t('common.no')}
                  size="small"
                  variant={node.isVerified ? 'filled' : 'outlined'}
                />
              </ChainNodeStatus>
            </ChainNodeRow>
          </ChainItemComponent>
        );
      })}
    </ChainContainer>
  );
}
